/**
 * cd-judgeme-fetcher — shared helpers for the Judge.me widget HTML parsers.
 *
 * Loaded ahead of cd-all-reviews-fetcher.js and cd-product-reviews-fetcher.js,
 * which previously duplicated these helpers verbatim (and had drifted on
 * `upgradePicUrl` — see the comment on that function). Exposes a single
 * global namespace `window.CdJudgemeFetcher` to keep the existing IIFE
 * pattern of the consumer scripts without forcing them to ESM.
 *
 * Idempotent: if loaded twice (both consumers on the same page) the
 * second registration silently no-ops.
 *
 * Exports:
 *   pickAttr(html, name) — read a single-quoted HTML attribute value
 *   pickText(html, openTag, closeTag) — slice + decode + strip tags
 *   formatDate(raw) — "2024-01-15 12:00:00 UTC" → "Jan 15, 2024"
 *   escapeHtml(s) — entity-escape for safe re-insertion into HTML
 *   decodeEntities(s) — reverse of escapeHtml; for already-encoded text
 *   upgradePicUrl(raw, target) — rewrite Imgix w=/h=/dpr= in place
 */
(function () {
  if (window.CdJudgemeFetcher) return;

  function pickAttr(html, name) {
    var re = new RegExp(name + "='([^']*)'");
    var m = re.exec(html);
    return m ? m[1] : '';
  }

  function pickText(html, openTag, closeTag) {
    var i = html.indexOf(openTag);
    if (i === -1) return '';
    var start = i + openTag.length;
    var end = html.indexOf(closeTag, start);
    if (end === -1) return '';
    return html.slice(start, end)
      .replace(/<[^>]+>/g, '')
      .replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"').replace(/&#39;/g, "'").trim();
  }

  function formatDate(raw) {
    if (!raw) return '';
    var d = new Date(raw.replace(' UTC', 'Z').replace(' ', 'T'));
    if (isNaN(d.getTime())) return raw.split(' ')[0] || '';
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }

  function escapeHtml(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  /**
   * Decode common HTML entities. Judge.me's widget HTML escapes
   * apostrophes/ampersands/etc inside attribute values and text nodes
   * (e.g., product titles like "Vincent&#39;s Voyage"). If we send
   * that through escapeHtml on render, the leading `&` gets re-encoded
   * to `&amp;` and the browser shows the literal `&#39;` instead of
   * `'`. Decode first, then re-escape on output → single, correct
   * encoding.
   */
  function decodeEntities(s) {
    return String(s == null ? '' : s)
      .replace(/&#39;/g, "'")
      .replace(/&#x27;/gi, "'")
      .replace(/&quot;/g, '"')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, '&');
  }

  /**
   * Upgrade a Judge.me Imgix URL's `w=`, `h=`, and `dpr=` params via
   * regex so any param position works (start/middle/end). Canonical
   * implementation — earlier all-reviews fetcher relied on Imgix's
   * last-wins behavior by appending `&w=N&dpr=2`, which worked by
   * accident and produced different URLs than the regex version. Now
   * unified here.
   */
  function upgradePicUrl(raw, target) {
    if (!raw) return raw;
    var t = String(target);
    var url = raw
      // Judge.me migrated review photos from judgeme.imgix.net (?w=) to
      // review-images.judge.me (?width=). The widget HTML carries the param
      // separator HTML-escaped as `&amp;`; left in place it gets re-escaped by
      // escapeHtml() to `&amp;amp;` and the new CDN 403s on the malformed URL
      // (the old imgix CDN silently ignored it). Decode it to a clean `&` so
      // the single downstream escapeHtml() yields a valid `&amp;` entity.
      .replace(/&amp;/g, '&')
      // Resize param — handle both the old imgix `w=`/`h=` and the new CDN
      // `width=`/`height=`. dpr only existed on the old imgix URLs.
      .replace(/([?&])w=\d{1,4}\b/, '$1w=' + t)
      .replace(/([?&])width=\d{1,4}\b/, '$1width=' + t)
      .replace(/([?&])h=\d{1,4}\b/, '$1h=' + t)
      .replace(/([?&])height=\d{1,4}\b/, '$1height=' + t)
      .replace(/([?&])dpr=\d+(\.\d+)?\b/, '$1dpr=2');
    // The new review-images.judge.me CDN serves JPEG and ignores auto=format;
    // force webp (universal browser support) to roughly halve the bytes. Old
    // imgix URLs already carry auto=format, so only touch the new CDN.
    if (url.indexOf('review-images.judge.me') !== -1 && url.indexOf('format=') === -1) {
      url += (url.indexOf('?') !== -1 ? '&' : '?') + 'format=webp';
    }
    return url;
  }

  window.CdJudgemeFetcher = {
    pickAttr: pickAttr,
    pickText: pickText,
    formatDate: formatDate,
    escapeHtml: escapeHtml,
    decodeEntities: decodeEntities,
    upgradePicUrl: upgradePicUrl
  };
})();
